// src/components/Chat AI/GenAIOptions.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Select,
  Slider,
  Toggle,
  Box,
  Button,
  Popover,
  StatusIndicator,
} from "@cloudscape-design/components";

import { chatAiApi } from "../../services/ChatAiApi";

// Model-specific constraints with dynamic thresholds and smart retrieval defaults
const MODEL_CONSTRAINTS = {
  "anthropic.claude-3-5-sonnet-20241022-v2:0": {
    temperature: {
      min: 0,
      max: 1,
      default: 0.7,
      scale: 100,
      step: 1,
      recommended: { min: 0.1, max: 0.9 },
    },
    topP: {
      min: 0,
      max: 1,
      default: 0.9,
      scale: 100,
      step: 1,
      recommended: { min: 0.1, max: 1.0 },
    },
    maxTokens: {
      min: 1,
      max: 200000,
      default: 2048,
      step: 1,
      recommended: { min: 100, max: 8000 },
    },
    topK: {
      min: 0,
      max: 500,
      default: 250,
      scale: 1,
      step: 1,
      recommended: { min: 1, max: 500 },
    },
    maxRetrievedContexts: {
      min: 1,
      max: 20,
      default: 5,
      recommended: { min: 3, max: 10 },
    },
    smartRetrieval: {
      default: true,
      recommended: true,
    },
  },
  "anthropic.claude-3-7-sonnet-20250219-v1:0": {
    temperature: {
      min: 0,
      max: 1,
      default: 0.7,
      scale: 100,
      step: 1,
      recommended: { min: 0.1, max: 0.9 },
    },
    topP: {
      min: 0,
      max: 1,
      default: 0.9,
      scale: 100,
      step: 1,
      recommended: { min: 0.1, max: 1.0 },
    },
    maxTokens: {
      min: 1,
      max: 100000,
      default: 2048,
      step: 1,
      recommended: { min: 100, max: 8000 },
    },
    topK: {
      min: 0,
      max: 500,
      default: 250,
      scale: 1,
      step: 1,
      recommended: { min: 1, max: 500 },
    },
    maxRetrievedContexts: {
      min: 1,
      max: 20,
      default: 5,
      recommended: { min: 3, max: 10 },
    },
    smartRetrieval: {
      default: true,
      recommended: true,
    },
  },
  "anthropic.claude-sonnet-4-20250514-v1:0": {
    temperature: {
      min: 0,
      max: 1,
      default: 0.6,
      scale: 100,
      step: 1,
      recommended: { min: 0.1, max: 0.8 },
    },
    topP: {
      min: 0,
      max: 1,
      default: 0.95,
      scale: 100,
      step: 1,
      recommended: { min: 0.1, max: 1.0 },
    },
    maxTokens: {
      min: 1,
      max: 400000,
      default: 8192,
      step: 1,
      recommended: { min: 500, max: 20000 },
    },
    topK: {
      min: 0,
      max: 500,
      default: 200,
      scale: 1,
      step: 1,
      recommended: { min: 1, max: 400 },
    },
    maxRetrievedContexts: {
      min: 1,
      max: 30,
      default: 10,
      recommended: { min: 5, max: 20 },
    },
    smartRetrieval: {
      default: true,
      recommended: true,
    },
  },
};

// Default constraints for models that don't have specific configurations
const DEFAULT_MODEL_CONSTRAINTS = {
  temperature: {
    min: 0,
    max: 1,
    default: 0.7,
    scale: 100,
    step: 1,
    recommended: { min: 0.1, max: 0.9 },
  },
  topP: {
    min: 0,
    max: 1,
    default: 0.9,
    scale: 100,
    step: 1,
    recommended: { min: 0.1, max: 1.0 },
  },
  maxTokens: {
    min: 1,
    max: 200000,
    default: 2048,
    step: 1,
    recommended: { min: 100, max: 8000 },
  },
  topK: {
    min: 0,
    max: 500,
    default: 250,
    scale: 1,
    step: 1,
    recommended: { min: 1, max: 500 },
  },
  maxRetrievedContexts: {
    min: 1,
    max: 20,
    default: 5,
    recommended: { min: 3, max: 10 },
  },
  smartRetrieval: {
    default: true,
    recommended: true,
  },
};

// Definitions for tooltips
const definitions = {
  model: {
    title: "AI Model Selection",
    content:
      "Choose the AI model that best fits your needs. Different models have different capabilities and specializations.",
  },
  maxRetrievedContexts: {
    title: "Maximum Retrieved Contexts",
    content:
      "Controls how many pieces of relevant information the model can reference. Higher values provide more context but may increase processing time.",
  },
  maxTokens: {
    title: "Maximum Token Generation",
    content:
      "Sets the maximum length of the generated response. One token is approximately 4 characters or 0.75 words. Increase for longer responses.",
  },
  temperature: {
    title: "Temperature",
    content:
      "Controls randomness in the response. Higher values make output more creative but less predictable. Lower values make output more focused and deterministic.",
  },
  topP: {
    title: "Top P (Nucleus Sampling)",
    content:
      "Controls diversity of word choice. Higher values consider more options, leading to more varied responses. Lower values make responses more focused.",
  },
  topK: {
    title: "Top K",
    content:
      "Limits the number of words the model considers for each choice. Higher values allow more variety, lower values make output more predictable.",
  },
  smartRetrieval: {
    title: "Smart Retrieval",
    content:
      "Enables advanced context selection and filtering. This helps ensure the most relevant information is used in generating responses.",
  },
};

const InfoPopover = ({ title, content }) => (
  <Popover
    dismissButton={false}
    position="right"
    size="medium"
    triggerType="custom"
    content={
      <Box padding="s">
        <SpaceBetween size="xs">
          <Box variant="h5">{title}</Box>
          <Box variant="p">{content}</Box>
        </SpaceBetween>
      </Box>
    }
  >
    <Button
      variant="inline-icon"
      iconName="status-info"
      ariaLabel={`Information about ${title}`}
    />
  </Popover>
);

const renderFormField = (key, label, component, definitions) => (
  <FormField
    label={
      <SpaceBetween direction="horizontal" size="xs">
        <span>{label}</span>
        <InfoPopover {...definitions[key]} />
      </SpaceBetween>
    }
  >
    {component}
  </FormField>
);

function GenAIOptions({ onOptionsChange }) {
  const [model, setModel] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [modelError, setModelError] = useState(null);
  const [modelConstraints, setModelConstraints] = useState(null);
  const [maxRetrievedContexts, setMaxRetrievedContexts] = useState(3);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [temperature, setTemperature] = useState(70);
  const [topP, setTopP] = useState(90);
  const [topK, setTopK] = useState(50);
  const [smartRetrieval, setSmartRetrieval] = useState(true);
  const [modelJustChanged, setModelJustChanged] = useState(false);

  // Function to reset all parameters to model defaults
  const resetToDefaults = () => {
    if (modelConstraints) {
      setTemperature(
        modelConstraints.temperature.default *
          (modelConstraints.temperature.scale || 1)
      );
      setTopP(
        modelConstraints.topP.default * (modelConstraints.topP.scale || 1)
      );
      setMaxTokens(modelConstraints.maxTokens.default);
      setTopK(modelConstraints.topK.default);

      if (modelConstraints.maxRetrievedContexts) {
        setMaxRetrievedContexts(modelConstraints.maxRetrievedContexts.default);
      }

      if (modelConstraints.smartRetrieval) {
        setSmartRetrieval(modelConstraints.smartRetrieval.default);
      }

      console.log("Reset to model defaults for:", model?.label);
    }
  };

  // Error handler for ResizeObserver
  useEffect(() => {
    const handler = (event) => {
      if (
        event.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  // Fetch models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setIsLoadingModels(true);
        setModelError(null);

        const data = await chatAiApi.getAvailableModels();
        console.log("Models response:", data);
        console.log("Available models from API:", data.availableModels);

        if (data.availableModels && Array.isArray(data.availableModels)) {
          const formattedModels = data.availableModels.map((model) => ({
            label: model.name || model.label || model.id,
            value: model.id || model.value,
            // Include other properties if needed for display or functionality
            provider: model.provider,
            description: model.description,
            payloadFormat: model.payloadFormat,
          }));

          // Log each model ID to help identify Claude Sonnet 4
          console.log(
            "Formatted models:",
            formattedModels.map((m) => ({ label: m.label, value: m.value }))
          );

          // Special check for Claude Sonnet 4
          const claudeSonnet4 = formattedModels.find(
            (m) => m.value === "anthropic.claude-sonnet-4-20250514-v1:0"
          );

          if (claudeSonnet4) {
            console.log(
              "✅ Claude Sonnet 4 FOUND in API response:",
              claudeSonnet4
            );
          } else {
            console.log("❌ Claude Sonnet 4 NOT FOUND in API response");
            console.log("🔍 Looking for similar models...");

            const similarModels = formattedModels.filter(
              (m) =>
                m.value.includes("claude") &&
                (m.value.includes("sonnet") || m.value.includes("4"))
            );

            if (similarModels.length > 0) {
              console.log("🔍 Found similar Claude models:", similarModels);
            } else {
              console.log("🔍 No Claude Sonnet models found at all");
            }
          }

          setAvailableModels(formattedModels);
          if (formattedModels.length > 0) {
            // Set Claude 3.7 as default if available, otherwise use first model
            const defaultModel =
              formattedModels.find(
                (m) => m.value === "anthropic.claude-3-7-sonnet-20250219-v1:0"
              ) || formattedModels[0];
            setModel(defaultModel);
          }
        } else {
          throw new Error("Invalid models data format");
        }
      } catch (error) {
        console.error("Error fetching models:", error);
        setModelError(error.message);
      } finally {
        setIsLoadingModels(false);
      }
    };

    fetchModels();
  }, []);

  // Update model constraints when model changes
  useEffect(() => {
    console.log("Model changed:", model);
    if (model?.value) {
      const constraints =
        MODEL_CONSTRAINTS[model.value] || DEFAULT_MODEL_CONSTRAINTS;
      console.log("Model constraints:", constraints);
      console.log(
        "Using default constraints:",
        !MODEL_CONSTRAINTS[model.value]
      );
      setModelConstraints(constraints);
      setModelJustChanged(true);

      // Update parameters to match model constraints and defaults
      if (constraints) {
        // Set all parameters to model-specific defaults
        setTemperature(
          constraints.temperature.default * (constraints.temperature.scale || 1)
        );
        setTopP(constraints.topP.default * (constraints.topP.scale || 1));
        setMaxTokens(constraints.maxTokens.default);
        setTopK(constraints.topK.default);

        // Set model-specific defaults for maxRetrievedContexts and smartRetrieval
        if (constraints.maxRetrievedContexts) {
          setMaxRetrievedContexts(constraints.maxRetrievedContexts.default);
        }

        if (constraints.smartRetrieval) {
          setSmartRetrieval(constraints.smartRetrieval.default);
        }

        console.log("Applied model defaults:", {
          temperature: constraints.temperature.default,
          topP: constraints.topP.default,
          maxTokens: constraints.maxTokens.default,
          topK: constraints.topK.default,
          maxRetrievedContexts: constraints.maxRetrievedContexts?.default,
          smartRetrieval: constraints.smartRetrieval?.default,
        });
      }

      // Clear the "just changed" flag after a short delay
      setTimeout(() => setModelJustChanged(false), 2000);
    }
  }, [model]);

  // Update parent when options change (silent updates)
  useEffect(() => {
    if (model) {
      // Get current model constraints (use default if specific model constraints not found)
      const currentConstraints =
        MODEL_CONSTRAINTS[model.value] || DEFAULT_MODEL_CONSTRAINTS;

      const options = {
        model: model?.value,
        maxRetrievedContexts,
        maxTokens,
        temperature: currentConstraints
          ? temperature / (currentConstraints.temperature.scale || 1)
          : temperature,
        topP: currentConstraints
          ? topP / (currentConstraints.topP.scale || 1)
          : topP,
        topK,
        smartRetrieval,
      };

      onOptionsChange(options);
    }
  }, [
    model,
    maxRetrievedContexts,
    maxTokens,
    temperature,
    topP,
    topK,
    smartRetrieval,
    onOptionsChange,
  ]);

  const renderSlider = (key, label, value, setValue) => {
    if (!modelConstraints || !modelConstraints[key]) {
      return null;
    }

    const {
      min,
      max,
      scale = 1,
      recommended,
      step = 1,
    } = modelConstraints[key];
    const actualMin = min * scale;
    const actualMax = max * scale;
    const actualStep = step;

    // Create enhanced label with recommended range info
    const enhancedLabel = (
      <SpaceBetween direction="vertical" size="xs">
        <SpaceBetween direction="horizontal" size="xs">
          <span>{label}</span>
          <InfoPopover {...definitions[key]} />
        </SpaceBetween>
        {recommended && (
          <Box variant="small" color="text-body-secondary">
            Recommended:{" "}
            {scale > 1
              ? `${recommended.min * scale}-${recommended.max * scale}`
              : `${recommended.min}-${recommended.max}`}
          </Box>
        )}
      </SpaceBetween>
    );

    return (
      <FormField label={enhancedLabel}>
        <SpaceBetween size="xs">
          <Slider
            min={actualMin}
            max={actualMax}
            value={value}
            onChange={({ detail }) => setValue(detail.value)}
            step={actualStep}
          />
          <Box variant="small" color="text-body-secondary">
            Current: {scale > 1 ? (value / scale).toFixed(2) : value}
            {recommended && (
              <span
                style={{
                  color:
                    value >= recommended.min * scale &&
                    value <= recommended.max * scale
                      ? "#0073bb"
                      : "#d13212",
                }}
              >
                {value >= recommended.min * scale &&
                value <= recommended.max * scale
                  ? " ✓ Within recommended range"
                  : " ⚠ Outside recommended range"}
              </span>
            )}
          </Box>
        </SpaceBetween>
      </FormField>
    );
  };

  return (
    <Container header={<Header variant="h2">GenAI Options</Header>}>
      <SpaceBetween size="l">
        {/* Model Selection */}
        {renderFormField(
          "model",
          <span>Model Selection {isLoadingModels ? "(Loading...)" : ""}</span>,
          <Select
            selectedOption={model}
            onChange={({ detail }) => setModel(detail.selectedOption)}
            options={availableModels}
            placeholder="Choose a model"
            loading={isLoadingModels}
            loadingText="Loading available models..."
            errorText={modelError}
            expandToViewport
            onLoadItems={() => {}}
            empty={
              <Box textAlign="center" color="inherit">
                <StatusIndicator type="error">
                  No models available
                </StatusIndicator>
              </Box>
            }
          />,
          definitions
        )}

        {/* Smart Retrieval Toggle with model-specific recommendations */}
        <FormField
          label={
            <SpaceBetween direction="vertical" size="xs">
              <SpaceBetween direction="horizontal" size="xs">
                <span>Smart Retrieval</span>
                <InfoPopover {...definitions.smartRetrieval} />
              </SpaceBetween>
              {modelConstraints?.smartRetrieval && (
                <Box variant="small" color="text-body-secondary">
                  Recommended for {model?.label}:{" "}
                  {modelConstraints.smartRetrieval.recommended
                    ? "Enabled"
                    : "Disabled"}
                </Box>
              )}
            </SpaceBetween>
          }
        >
          <SpaceBetween size="xs">
            <Toggle
              onChange={({ detail }) => setSmartRetrieval(detail.checked)}
              checked={smartRetrieval}
            >
              {smartRetrieval ? "Enabled" : "Disabled"}
            </Toggle>
            {modelConstraints?.smartRetrieval && (
              <Box variant="small" color="text-body-secondary">
                <span
                  style={{
                    color:
                      smartRetrieval ===
                      modelConstraints.smartRetrieval.recommended
                        ? "#0073bb"
                        : "#d13212",
                  }}
                >
                  {smartRetrieval ===
                  modelConstraints.smartRetrieval.recommended
                    ? "✓ Matches model recommendation"
                    : "⚠ Differs from model recommendation"}
                </span>
              </Box>
            )}
          </SpaceBetween>
        </FormField>

        {/* Show parameter controls only when Smart Retrieval is OFF */}
        {!smartRetrieval && modelConstraints && (
          <>
            {/* Max Retrieved Contexts - Dynamic based on model */}
            {renderSlider(
              "maxRetrievedContexts",
              "Max Retrieved Contexts",
              maxRetrievedContexts,
              setMaxRetrievedContexts
            )}

            {/* Max Tokens Slider */}
            {renderSlider(
              "maxTokens",
              "Max Token Generation",
              maxTokens,
              setMaxTokens
            )}

            {/* Temperature Slider */}
            {renderSlider(
              "temperature",
              "Temperature",
              temperature,
              setTemperature
            )}

            {/* Top P Slider */}
            {renderSlider("topP", "Top P", topP, setTopP)}

            {/* Top K Slider */}
            {renderSlider("topK", "Top K", topK, setTopK)}
          </>
        )}

        {/* Information message when Smart Retrieval is enabled */}
        {smartRetrieval && (
          <Box variant="awsui-key-label" padding="s" color="text-status-info">
            <SpaceBetween direction="horizontal" size="xs">
              <StatusIndicator type="info">
                Smart Retrieval is enabled. Model parameters are automatically
                optimized.
              </StatusIndicator>
            </SpaceBetween>
          </Box>
        )}

        {/* Model Change Notification and Reset Button */}
        {modelJustChanged && (
          <Box variant="awsui-key-label" padding="s" color="text-status-info">
            <SpaceBetween direction="horizontal" size="xs">
              <StatusIndicator type="info">
                Model settings updated to {model?.label} defaults
              </StatusIndicator>
            </SpaceBetween>
          </Box>
        )}

        {/* Reset to Defaults Button */}
        {modelConstraints && (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="normal"
              iconName="refresh"
              onClick={resetToDefaults}
            >
              Reset to Model Defaults
            </Button>
            <Box variant="small" color="text-body-secondary">
              Restore all parameters to recommended defaults for {model?.label}
            </Box>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
}

export default GenAIOptions;